import { CreatePostImage } from "@/core/types/postTypes";
import { MediaContentType } from "@/core/types/mediaContentType";
import { fileCacheService } from "@/core/cache/fileCacheService";
import { API_CONFIG } from "@/config/api";
import { UserRole, UserRoleValue } from "@/services/urlService";

// Wire + computed data structure (camelCase). The backend now emits camelCase
// for all JSON, so this single interface covers both the inbound wire shape and
// the computed values the model derives (URLs, defaults).
export interface MediaItemData {
  // Core identity
  id: number;
  clientId: string;
  storageId: string;

  // Basic file info
  originalFilename: string;
  mediaType: "photo" | "video" | "audio";

  localUrl: string;
  folderInfo?: any; // Direct folder info structure from backend
  contentType: string;

  // File metadata
  fileSize: number | null;
  duration?: number | null;
  width?: number;
  height?: number;
  aspectRatio?: string;
  durationSeconds?: number | null;
  rotationDegrees?: number | null; // Manual rotation correction (0, 90, 180, 270)

  // Analysis scores (legacy)
  analysisScore?: string | null;
  instagramScore?: string | null;

  // AI Analysis data (new)
  aiAnalysis?: {
    instagramAppealScore: number | null;
    facesScore: number | null;
    colorAppeal: number | null;
    compositionQuality: number | null;
    imageQuality: number | null;
    engagementPrediction: number | null;
    formatOptimization: number | null;
    brandConsistency: number | null;
    visualComplexity: number | null;
    seasonalRelevance: number | null;
    trendsScore: number | null;
    labelDescription: string | null;
    analyzedAt: string | null;
  } | null;

  // Timestamps
  createdAt: string;
  updatedAt: string;
  processedAt: string | null;

  // Stage information (computed from domain)
  stage?: string;
  stageConfidence?: number;

  // Content group (legacy - derived from folder)
  group?: string;
  groupConfidence?: number;

  // Client approval label
  label?: string;

  // Client rating (1-5 stars)
  clientRating?: number | null;
  clientComment?: string | null;
  clientReviewedAt?: string | null;

  // Processing status (computed from domain)
  processingStatus?:
    | "completed"
    | "processing"
    | "failed"
    | "pending"
    | "unknown";
  isReady?: boolean;

  // Format classifications
  isPostFormat?: boolean;
  isReelFormat?: boolean;
  isStoryFormat?: boolean;
  optimalUseCases?: string[];

  // Management status
  isPostManaged?: boolean;
  canChangeStage?: boolean;
  isReadyForUse?: boolean;

  // Public sharing
  publicStatus?: "copying" | "public" | null;

  // Website tags
  websiteTags: Array<{ tag: string; properties: Record<string, string> }>;

  // Post relationships (optional)
  postRelationships?: Array<{
    postId: string;
    position: number;
    isThumbnail: boolean;
  }>;

  // Raw status enum value from backend (used by display components)
  status?: string;

  // Per-post crop override surfaced on items inside a post's media. Lives on the
  // post-media junction row, not the underlying media item — only populated when
  // reading a post.
  cropRect?: {
    x: number;
    y: number;
    width: number;
    height: number;
    /** Pixel space the rect was measured in; null on legacy rects. */
    sourceWidth?: number | null;
    sourceHeight?: number | null;
  } | null;

  // Usage across posts
  usedInPosts?: Array<{
    postId: string;
    position: number;
    isThumbnail: boolean;
  }>;
  usageCount?: number;

  // Legacy fields (for backward compatibility)
  isTaken: boolean;
  isProcessed: boolean;
  position: number;
  postId?: string;
  stageHashtag?: string;
  contentCategory?: "image" | "video" | "audio";
  videoType?: "stories" | "reels";
  thumbnailUrl?: string;
  streamUrl?: string;
  mimeType?: string;
  processingAttempts?: number;
  lastError?: string | null;
  thumbnailStatus?:
    | "generated"
    | "generating"
    | "pending_video"
    | "not_applicable";
}

export class MediaItem {
  private static readonly API_URL = API_CONFIG.URL;

  // Raw wire data (camelCase), without derived URLs or defaults. Retained
  // because external call sites and clone() reference it directly.
  readonly backendData: MediaItemData;
  // Computed view: wire data plus derived URLs and defaulted values.
  readonly frontendData: MediaItemData;

  private context: UserRoleValue;
  private clientId: string;
  private authToken?: string;

  constructor(
    data: MediaItemData,
    context: UserRoleValue,
    clientId: string,
    authToken?: string,
  ) {
    this.backendData = { ...data };
    this.context = context;
    this.clientId = clientId;
    this.authToken = authToken;

    // Computed view: spread the camelCase wire data, then override the derived
    // URLs and apply defaults. The mapping layer is gone — only domain logic
    // (URL building, derived duration, fallbacks) remains.
    this.frontendData = {
      ...data,
      localUrl: this.generateLocalUrl(data),
      thumbnailUrl: this.generateThumbnailUrl(data),
      streamUrl: this.generateStreamUrl(data),
      duration: data.duration ?? data.durationSeconds ?? null,
      aiAnalysis: data.aiAnalysis ?? null,
      stage: data.stage ?? undefined,
      label: data.label ?? undefined,
      clientRating: data.clientRating ?? null,
      clientComment: data.clientComment ?? null,
      clientReviewedAt: data.clientReviewedAt ?? null,
      processingStatus: data.processingStatus || "unknown",
      publicStatus: data.publicStatus ?? null,
      websiteTags: data.websiteTags ?? [],
      isTaken: data.isTaken ?? data.isPostManaged ?? false,
      isProcessed: data.isProcessed ?? false,
      position: data.position ?? 0,
      videoType: data.videoType ?? (data.contentType as "stories" | "reels"),
      processingAttempts: data.processingAttempts || 0,
      thumbnailStatus: data.thumbnailStatus || "not_applicable",
    };
  }

  private generateLocalUrl(data: MediaItemData): string {
    // Cache-busting parameter based on updatedAt
    const cacheBust = data.updatedAt ? `?v=${new Date(data.updatedAt).getTime()}` : '';

    if (data.mediaType === "video") {
      // For videos, local_url points to thumbnail for display
      return this.generateThumbnailUrl(data) || "";
    } else if (data.mediaType === "audio") {
      // Audio uses stream URL
      return this.processUrl(
        `/client/${this.clientId}/audio/${data.id}/stream`
      );
    } else {
      // For images, use context-specific endpoints
      if (this.context === UserRole.CLIENT || this.context === UserRole.EDITOR) {
        return this.processUrl(`/api/media/${data.id}/image${cacheBust}`);
      } else {
        return this.processUrl(`/client/${this.clientId}/image/${data.id}${cacheBust}`);
      }
    }
  }

  private generateStreamUrl(data: MediaItemData): string | undefined {
    // Only generate for videos
    if (data.mediaType !== "video") return undefined;

    // If we have context and clientId, always generate proper authenticated URLs
    // This takes priority over backend-provided URLs to avoid using deprecated endpoints
    if (this.context && this.clientId) {
      if (this.context === UserRole.CLIENT) {
        // Client portal context uses /portal/client/ prefix
        return this.processUrl(
          `/portal/client/${this.clientId}/video/${data.id}/stream`,
        );
      } else if (this.context === UserRole.EDITOR) {
        // Editor context uses /editor/ prefix
        return this.processUrl(
          `/editor/${this.clientId}/video/${data.id}/stream`,
        );
      } else {
        // Admin context uses original /client/ prefix (Google OAuth)
        return this.processUrl(
          `/client/${this.clientId}/video/${data.id}/stream`,
        );
      }
    }

    return undefined;
  }

  /**
   * Generate cache-busting query parameter for video thumbnails
   * Uses processedAt timestamp (preferred) or rotationDegrees as fallback
   */
  private static generateVideoCacheBuster(data: MediaItemData): string {
    if (data.processedAt) {
      return `?v=${new Date(data.processedAt).getTime()}`;
    }
    if (data.rotationDegrees !== undefined && data.rotationDegrees !== null) {
      return `?v=r${data.rotationDegrees}`;
    }
    return "";
  }

  /**
   * Construct video thumbnail URL with proper context and cache-busting
   */
  private static buildVideoThumbnailUrl(
    context: string,
    clientId: string,
    mediaItemId: number,
    data: MediaItemData,
  ): string {
    const cacheBuster = MediaItem.generateVideoCacheBuster(data);

    if (context === UserRole.CLIENT || context === UserRole.EDITOR) {
      // Portal contexts use unified media endpoint
      return `/api/media/${mediaItemId}/thumbnail${cacheBuster}`;
    } else {
      // Admin context uses existing endpoint with file identity (Google OAuth)
      const videoId = data.storageId || mediaItemId;
      return `/client/${clientId}/video/${videoId}/thumbnail${cacheBuster}`;
    }
  }

  /**
   * Construct image thumbnail URL (same as main image) with cache-busting
   */
  private static buildImageThumbnailUrl(
    context: string,
    clientId: string,
    mediaItemId: number,
    data?: MediaItemData,
  ): string {
    const cacheBust = data?.updatedAt ? `?v=${new Date(data.updatedAt).getTime()}` : '';

    if (context === UserRole.CLIENT || context === UserRole.EDITOR) {
      // Portal contexts use unified media endpoint
      return `/api/media/${mediaItemId}/image${cacheBust}`;
    } else {
      // Admin context uses existing endpoint (Google OAuth)
      return `/client/${clientId}/image/${mediaItemId}${cacheBust}`;
    }
  }

  private generateThumbnailUrl(
    data: MediaItemData,
  ): string | undefined {
    // If we have context and clientId, always generate proper authenticated URLs
    // This takes priority over backend-provided URLs to avoid using deprecated endpoints
    if (this.context && this.clientId) {
      if (data.mediaType === "video") {
        return this.processUrl(
          MediaItem.buildVideoThumbnailUrl(this.context, this.clientId, data.id, data)
        );
      } else {
        return this.processUrl(
          MediaItem.buildImageThumbnailUrl(this.context, this.clientId, data.id, data)
        );
      }
    }

    // Fallback to backend-provided URL if available
    return data.thumbnailUrl
      ? this.processUrl(data.thumbnailUrl)
      : undefined;
  }

  private processUrl(url: string): string {
    if (!url) return "";

    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }

    let processedUrl = url;

    // Legacy URL transformation removed - URLs are derived by context in the constructor

    const fullUrl = processedUrl.startsWith("/")
      ? `${MediaItem.API_URL}${processedUrl}`
      : `${MediaItem.API_URL}/${processedUrl}`;

    return fullUrl;
  }

  // Getters
  get id(): number {
    return this.frontendData.id;
  }
  get storageId(): string {
    return this.frontendData.storageId;
  }
  get fileIdentity(): string {
    return this.frontendData.storageId || String(this.frontendData.id);
  }
  get originalFilename(): string {
    return this.frontendData.originalFilename;
  }
  get fileSize(): number | null {
    return this.frontendData.fileSize;
  }
  get mediaType(): "photo" | "video" | "audio" {
    return this.frontendData.mediaType;
  }
  get localUrl(): string {
    return this.frontendData.localUrl;
  }
  get createdAt(): string {
    return this.frontendData.createdAt;
  }
  get position(): number {
    return this.frontendData.position;
  }
  get folderInfo() {
    return this.frontendData.folderInfo;
  }
  get stage(): string | undefined {
    return this.frontendData.stage;
  }
  get group(): string | undefined {
    return this.frontendData.group;
  }
  get label(): string | undefined {
    return this.frontendData.label;
  }
  get clientRating(): number | null | undefined {
    return this.frontendData.clientRating;
  }
  get clientComment(): string | null | undefined {
    return this.frontendData.clientComment;
  }
  get clientReviewedAt(): string | null | undefined {
    return this.frontendData.clientReviewedAt;
  }
  get thumbnailUrl(): string | undefined {
    return this.frontendData.thumbnailUrl;
  }
  get mimeType(): string | undefined {
    return this.frontendData.mimeType;
  }
  get streamUrl(): string | undefined {
    return this.frontendData.streamUrl;
  }
  get processingStatus():
    | "completed"
    | "processing"
    | "failed"
    | "pending"
    | "unknown" {
    return this.frontendData.processingStatus || "unknown";
  }
  get processingAttempts(): number {
    return this.frontendData.processingAttempts || 0;
  }
  get lastError(): string | null {
    return this.frontendData.lastError || null;
  }
  get width(): number | undefined {
    return this.frontendData.width;
  }
  get height(): number | undefined {
    return this.frontendData.height;
  }
  get rotationDegrees(): number | null | undefined {
    return this.frontendData.rotationDegrees;
  }
  get isTaken(): boolean {
    return this.frontendData.isTaken;
  }
  get usedInPosts(): Array<{
    postId: string;
    position: number;
    isThumbnail: boolean;
  }> {
    return this.backendData.usedInPosts || [];
  }
  get usageCount(): number {
    return this.backendData.usageCount || 0;
  }
  get publicStatus(): "copying" | "public" | null {
    return this.frontendData.publicStatus ?? null;
  }
  get websiteTags(): Array<{ tag: string; properties: Record<string, string> }> {
    return this.frontendData.websiteTags;
  }

  get thumbnailStatus():
    | "generated"
    | "generating"
    | "pending_video"
    | "not_applicable" {
    return this.frontendData.thumbnailStatus || "not_applicable";
  }

  get aspectRatioInfo() {
    return this.frontendData.folderInfo?.aspectRatioInfo;
  }

  get durationSeconds(): number | null | undefined {
    return this.frontendData.durationSeconds;
  }

  // Method to get URL for authenticated requests (authentication via headers)
  getAuthenticatedUrl(baseUrl: string | undefined): string | undefined {
    if (!baseUrl) return undefined;

    // For client context, preserve token in URLs
    if (this.context === UserRole.CLIENT) {
      return baseUrl; // Don't strip any tokens for client context
    }

    // Remove any existing access_token parameters from legacy URLs (admin context only)
    let cleanUrl = baseUrl;
    cleanUrl = cleanUrl.replace(/[?&]access_token=[^&]*&?/g, "");
    cleanUrl = cleanUrl.replace(/[?&]$/, "");

    return cleanUrl;
  }

  // Method to get clean URL for use with apiClient/caching (no query auth)
  getCleanUrl(): string {
    return this.getAuthenticatedUrl(this.localUrl) || this.localUrl;
  }

  // Method to get clean thumbnail URL for use with apiClient/caching
  getCleanThumbnailUrl(): string {
    const baseUrl = this.thumbnailUrl || this.localUrl;
    return this.getAuthenticatedUrl(baseUrl) || baseUrl;
  }

  // Method to get clean stream URL for use with apiClient/caching
  getCleanStreamUrl(): string {
    const baseUrl = this.streamUrl || this.localUrl;
    return this.getAuthenticatedUrl(baseUrl) || baseUrl;
  }

  private getAccessToken(): string | null {
    // For editor/approval contexts, use the instance auth token if available
    if (this.authToken && this.authToken.length > 0) {
      return this.authToken;
    }

    // For admin context, get from localStorage
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        return user.accessToken || null;
      } catch {
        return null;
      }
    }
    return null;
  }

  // Method to get URL with query parameter authentication (for img tags)
  getUrlWithQueryAuth(): string {
    const baseUrl = this.localUrl;

    // Check if token is already in URL
    if (baseUrl.includes("access_token=")) {
      return baseUrl;
    }

    // Get token based on context
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      console.warn("⚠️ No access token available for image authentication");
      return baseUrl;
    }

    // Add token as query parameter (required for <img> tags since browsers don't send headers)
    const separator = baseUrl.includes("?") ? "&" : "?";
    const authenticatedUrl = `${baseUrl}${separator}access_token=${encodeURIComponent(accessToken)}`;

    return authenticatedUrl;
  }

  // Method to get authenticated thumbnail URL with query parameter authentication
  getThumbnailUrlWithQueryAuth(): string {
    const baseUrl = this.thumbnailUrl || this.localUrl;

    // Do not append token to GCS URLs, as they should be pre-signed by the backend
    if (baseUrl.includes("storage.googleapis.com")) {
      return baseUrl;
    }

    // Do not append access token if authentication is already present
    const accessTokenMatch = baseUrl.match(/access_token=([^&]*)/);
    if (accessTokenMatch && accessTokenMatch[1] && accessTokenMatch[1] !== "") {
      return baseUrl;
    }

    // Always get fresh access token for each URL generation
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      console.warn("⚠️ No access token available for thumbnail authentication");
      return baseUrl;
    }

    const separator = baseUrl.includes("?") ? "&" : "?";
    const authenticatedUrl = `${baseUrl}${separator}access_token=${encodeURIComponent(accessToken)}`;

    return authenticatedUrl;
  }

  // Method to get authenticated stream URL with query parameter authentication
  getStreamUrlWithQueryAuth(skipHeal: boolean = false): string {
    const baseUrl = this.streamUrl || this.localUrl;

    // Do not append token to GCS URLs, as they should be pre-signed by the backend
    if (baseUrl.includes("storage.googleapis.com")) {
      return baseUrl;
    }

    // Do not append access token if authentication is already present
    const accessTokenMatch = baseUrl.match(/access_token=([^&]*)/);
    if (accessTokenMatch && accessTokenMatch[1] && accessTokenMatch[1] !== "") {
      return baseUrl;
    }

    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return baseUrl;
    }

    const separator = baseUrl.includes("?") ? "&" : "?";
    let url = `${baseUrl}${separator}access_token=${encodeURIComponent(accessToken)}`;

    // Add skip_heal parameter for review mode (fast-path, no autoheal)
    if (skipHeal) {
      url += "&skip_heal=true";
    }

    return url;
  }

  getUrl(): string {
    return this.localUrl;
  }

  getDisplayUrl(): string {
    return this.getUrlWithQueryAuth();
  }

  // Get clean display URL for use with header authentication
  getCleanDisplayUrl(): string {
    return this.getCleanUrl();
  }

  private triggerDownload(url: string, filename: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async downloadFile(
    context: UserRoleValue,
    onProgress?: (status: 'checking' | 'downloading' | 'ready' | 'failed') => void
  ): Promise<void> {
    try {
      if (!this.clientId) {
        throw new Error('Client ID is required to download media item');
      }

      const accessToken = this.getAccessToken();
      if (!accessToken) {
        throw new Error('Access token is required to download media item');
      }

      const { getApiBaseUrl } = await import('@/utils/apiUtils');
      const apiBaseUrl = getApiBaseUrl();

      let downloadUrl: string;
      if (context === UserRole.ADMIN) {
        // Admin uses Google OAuth authenticated route
        downloadUrl = `${apiBaseUrl}/client/${this.clientId}/media/${this.id}/download`;
      } else {
        // Client and Editor use unified portal route with JWT authentication
        downloadUrl = `${apiBaseUrl}/api/media/${this.id}/download`;
      }

      // First, check if file is ready or needs processing
      onProgress?.('checking');

      const checkResponse = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        redirect: 'manual', // Don't follow redirects - we'll handle them
      });

      // If we get a redirect (302/303), file is ready - open it
      if (checkResponse.status === 302 || checkResponse.status === 303 || checkResponse.type === 'opaqueredirect') {
        onProgress?.('ready');

        // Use anchor tag instead of window.open to avoid iOS Safari popup blocker
        const urlWithAuth = `${downloadUrl}?access_token=${encodeURIComponent(accessToken)}`;
        this.triggerDownload(urlWithAuth, this.originalFilename || `media-${this.id}`);
        return;
      }

      // If we get 202 Accepted, file is being downloaded from Drive
      if (checkResponse.status === 202) {
        const data = await checkResponse.json();
        onProgress?.('downloading');

        // Poll for completion
        const pollInterval = data.retryAfter ? data.retryAfter * 1000 : 5000;
        const maxPolls = 24; // Max 2 minutes (24 * 5s)
        let pollCount = 0;

        const poll = async (): Promise<void> => {
          if (pollCount >= maxPolls) {
            throw new Error('Download timeout - file is still processing');
          }

          await new Promise(resolve => setTimeout(resolve, pollInterval));
          pollCount++;

          const pollResponse = await fetch(downloadUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
            redirect: 'manual',
          });

          if (pollResponse.status === 302 || pollResponse.status === 303 || pollResponse.type === 'opaqueredirect') {
            // File is ready - use anchor tag to avoid iOS Safari popup blocker
            onProgress?.('ready');
            const urlWithAuth = `${downloadUrl}?access_token=${encodeURIComponent(accessToken)}`;
            this.triggerDownload(urlWithAuth, this.originalFilename || `media-${this.id}`);
            return;
          } else if (pollResponse.status === 202) {
            // Still processing, continue polling
            return poll();
          } else {
            // Some error occurred
            const errorData = await pollResponse.json();
            throw new Error(errorData.error || 'Download failed');
          }
        };

        await poll();
        return;
      }

      // If we get here, there was an error
      if (!checkResponse.ok) {
        const errorData = await checkResponse.json();
        throw new Error(errorData.error || `Download failed: ${checkResponse.status}`);
      }

    } catch (error) {
      onProgress?.('failed');
      console.error('Error downloading file:', error);
      throw error;
    }
  }

  /**
   * Download compressed version of the media file (optimized WebP for images, proxy MP4 for videos).
   * Unlike downloadFile(), this doesn't trigger a download job - the file either exists or it doesn't.
   */
  async downloadCompressedFile(
    context: UserRoleValue,
    onProgress?: (status: 'checking' | 'ready' | 'failed' | 'not_available') => void
  ): Promise<void> {
    try {
      if (!this.clientId) {
        throw new Error('Client ID is required to download compressed file');
      }

      const accessToken = this.getAccessToken();
      if (!accessToken) {
        throw new Error('Access token is required to download compressed file');
      }

      const { getApiBaseUrl } = await import('@/utils/apiUtils');
      const apiBaseUrl = getApiBaseUrl();

      let downloadUrl: string;
      if (context === UserRole.ADMIN) {
        downloadUrl = `${apiBaseUrl}/client/${this.clientId}/media/${this.id}/download-compressed`;
      } else {
        downloadUrl = `${apiBaseUrl}/api/media/${this.id}/download-compressed`;
      }

      onProgress?.('checking');

      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        redirect: 'manual',
      });

      // If we get a redirect (302/303), file is ready - download it
      if (response.status === 302 || response.status === 303 || response.type === 'opaqueredirect') {
        onProgress?.('ready');
        const urlWithAuth = `${downloadUrl}?access_token=${encodeURIComponent(accessToken)}`;
        const filename = this.originalFilename || `media-${this.id}`;
        this.triggerDownload(urlWithAuth, filename);
        return;
      }

      // If 404, compressed version doesn't exist
      if (response.status === 404) {
        onProgress?.('not_available');
        return;
      }

      // Any other response is an error
      const errorData = await response.json();
      throw new Error(errorData.error || `Download failed: ${response.status}`);

    } catch (error) {
      onProgress?.('failed');
      console.error('Error downloading compressed file:', error);
      throw error;
    }
  }

  async deleteCompletely(): Promise<void> {
    if (!this.clientId) {
      throw new Error('Client ID is required to delete media item');
    }

    try {
      const accessToken = this.getAccessToken();
      if (!accessToken) {
        throw new Error('Access token is required to delete media item');
      }

      const response = await fetch(
        `${MediaItem.API_URL}/client/${this.clientId}/media/${this.id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete media item');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.errors?.[0] || 'Delete operation failed');
      }

      console.log(`✅ Media item ${this.id} deleted completely:`, result);
    } catch (error) {
      console.error('Error deleting media item:', error);
      throw error;
    }
  }

  async updateLabel(label: string): Promise<MediaItem> {
    if (!this.clientId) {
      throw new Error('Client ID is required to update media item label');
    }

    try {
      const accessToken = this.getAccessToken();
      if (!accessToken) {
        throw new Error('Access token is required to update media item label');
      }

      const response = await fetch(
        `${MediaItem.API_URL}/admin/media/${this.id}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            clientId: this.clientId,
            label: label,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update label');
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Update label operation failed');
      }

      console.log(`✅ Media item ${this.id} label updated to: "${label}"`);

      return MediaItem.fromBackend(
        result.mediaItem,
        this.context,
        this.clientId,
      );
    } catch (error) {
      console.error('Error updating media item label:', error);
      throw error;
    }
  }

  getImageForPost(): string {
    return this.getUrlWithQueryAuth();
  }

  /**
   * Get cached URL if available, otherwise return original URL
   * This method is async and should be used for instant display from cache
   * Ensures files are synced from Google Drive before attempting to cache
   */
  async getCachedUrl(clientId?: string): Promise<string> {
    try {
      // Try to get from cache first
      const cachedUrl = await fileCacheService.getCachedFile(
        this.id.toString(),
      );
      if (cachedUrl) {
        return cachedUrl; // blob:// URL for instant display
      }

      // For client context, use direct URL with token instead of apiClient caching
      if (this.context === UserRole.CLIENT) {
        return this.getUrlWithQueryAuth();
      }

      // If not cached and we have clientId, cache it now using clean URL (auth via headers)
      if (clientId) {
        return await fileCacheService.cacheFile(
          this.getCleanUrl(),
          this.id.toString(),
          clientId,
        );
      }

      // Fallback to authenticated URL
      return this.getUrlWithQueryAuth();
    } catch (error) {
      if (error instanceof Error && error.message.includes("File not found")) {
        console.warn(
          `Media item ${this.id} file not found on server, using fallback`,
        );
        // For missing files, return a placeholder or the original URL
        return this.getUrlWithQueryAuth();
      }
      console.error(`Error getting cached URL for ${this.id}:`, error);
      return this.getUrlWithQueryAuth();
    }
  }

  /**
   * Preload file to cache for faster future access
   */
  async preloadToCache(clientId: string): Promise<void> {
    try {
      // Check if already cached
      const isCached = await fileCacheService.isCached(this.id.toString());
      if (!isCached) {
        await fileCacheService.cacheFile(
          this.getCleanUrl(),
          this.id.toString(),
          clientId,
        );
      }
    } catch (error) {
      console.error(`Error preloading ${this.originalFilename}:`, error);
    }
  }

  /**
   * Check if this media item is cached
   */
  async isCached(): Promise<boolean> {
    try {
      return await fileCacheService.isCached(this.id.toString());
    } catch (error) {
      return false;
    }
  }

  getName(): string {
    return this.originalFilename;
  }

  isImage(): boolean {
    return this.mediaType === "photo";
  }

  isShortVideo(): boolean {
    return (
      this.mediaType === "video" &&
      this.frontendData.videoType === "stories"
    );
  }

  isLongVideo(): boolean {
    return (
      this.mediaType === "video" &&
      this.frontendData.videoType === "reels"
    );
  }

  isGeneralVideo(): boolean {
    return (
      this.mediaType === "video" &&
      this.frontendData.contentType === "videos"
    );
  }

  isPostVideo(): boolean {
    return (
      this.mediaType === "video" &&
      this.frontendData.contentType === "post_video"
    );
  }

  getContentType(): MediaContentType {
    if (this.frontendData.contentType) {
      return this.frontendData.contentType as MediaContentType;
    }

    if (this.mediaType === "photo") return "photos";
    if (this.mediaType === "audio") return "audio";
    if (this.mediaType === "video") {
      return this.frontendData.videoType || "videos";
    }
    return "photos";
  }

  hasValidContent(): boolean {
    return !!(this.localUrl && this.storageId);
  }

  isVideo(): boolean {
    return this.mediaType === "video";
  }

  isAudio(): boolean {
    return this.mediaType === "audio";
  }

  getAudioStreamUrl(): string {
    return this.processUrl(
      `/client/${this.clientId}/audio/${this.id}/stream`
    );
  }

  isViewable(): boolean {
    const viewableStages = ["ready", "next"];
    return viewableStages.includes(this.stage || "");
  }

  isProcessing(): boolean {
    return this.processingStatus === "processing";
  }

  isProcessingFailed(): boolean {
    return this.processingStatus === "failed";
  }

  isProcessingComplete(): boolean {
    return this.processingStatus === "completed";
  }

  isProcessed(): boolean {
    return this.frontendData.isProcessed ?? this.processingStatus === "completed";
  }

  isReadyForDisplay(): boolean {
    return this.isProcessed() && this.processingStatus === "completed";
  }

  // Video-specific thumbnail methods
  isThumbnailReady(): boolean {
    return this.thumbnailStatus === "generated";
  }

  isThumbnailGenerating(): boolean {
    return this.thumbnailStatus === "generating";
  }

  isThumbnailPending(): boolean {
    return this.thumbnailStatus === "pending_video";
  }

  getProcessingStatusText(): string {
    switch (this.processingStatus) {
      case "completed":
        return "Ready";
      case "processing":
        return `Processing (${this.processingAttempts}/3)`;
      case "failed":
        return "Failed";
      case "pending":
        return "Queued";
      case "unknown":
      default:
        return "Unknown";
    }
  }

  getAspectRatio(): number | null {
    if (!this.width || !this.height) return null;
    return this.width / this.height;
  }

  getAspectRatioCategory(): "4:5" | "9:16" | "other" | "unknown" {
    const ratio = this.getAspectRatio();
    if (!ratio) return "unknown";

    // 4:5 ratio = 0.8 (square-ish, Instagram feed)
    if (Math.abs(ratio - 0.8) < 0.1) return "4:5";

    // 9:16 ratio = 0.5625 (vertical, Instagram stories)
    if (Math.abs(ratio - 0.5625) < 0.1) return "9:16";

    return "other";
  }

  is4to5Ratio(): boolean {
    return this.getAspectRatioCategory() === "4:5";
  }

  is9to16Ratio(): boolean {
    return this.getAspectRatioCategory() === "9:16";
  }

  toCreatePostImage(): CreatePostImage {
    return {
      mediaItemId: this.id,
      originalId: this.fileIdentity,
      originalFilename: this.originalFilename,
      position: this.position,
      folderInfo: this.folderInfo
        ? {
            folderId: this.folderInfo.folderId,
            folderName: this.folderInfo.folderName,
          }
        : undefined,
    };
  }

  toJSON(): MediaItemData {
    return { ...this.frontendData };
  }

  clone(updates: Partial<MediaItemData> = {}): MediaItem {
    return new MediaItem(
      {
        ...this.backendData,
        ...updates,
      },
      this.context,
      this.clientId,
    );
  }

  withStage(newStage: string): MediaItem {
    return this.clone({
      stage: newStage,
      stageHashtag: `#${newStage}`,
    });
  }

  withWebsiteTags(
    tags: Array<{ tag: string; properties: Record<string, string> }>,
  ): MediaItem {
    return this.clone({ websiteTags: tags });
  }

  static fromBackend(
    data: MediaItemData,
    context: UserRoleValue,
    clientId: string,
    authToken?: string, // For client token, editor token, or admin access token
  ): MediaItem {
    // Wire is camelCase now, so no key mapping is needed. The constructor derives
    // all display URLs from context; this is a thin pass-through.
    if (context === UserRole.CLIENT && !authToken) {
      // Client context MUST have an explicit authToken.
      throw new Error(
        `MediaItem.fromBackend: Client context requires explicit authToken but none provided for item ${data.id}`,
      );
    }

    return new MediaItem(data, context, clientId, authToken);
  }

  static fromBackendArray(
    backendArray: MediaItemData[],
    context: UserRoleValue,
    clientId: string,
    authToken?: string,
  ): MediaItem[] {
    return backendArray.map((item) =>
      MediaItem.fromBackend(item, context, clientId, authToken),
    );
  }

  static sortByPosition(items: MediaItem[]): MediaItem[] {
    return [...items].sort((a, b) => {
      const posA = a.position ?? 999;
      const posB = b.position ?? 999;
      return posA - posB;
    });
  }

  static filterByStage(items: MediaItem[], stage: string): MediaItem[] {
    return items.filter((item) => item.stage === stage);
  }

  static groupByStage(items: MediaItem[]): Record<string, MediaItem[]> {
    return items.reduce(
      (groups, item) => {
        const stage = item.stage || "unknown";
        if (!groups[stage]) {
          groups[stage] = [];
        }
        groups[stage].push(item);
        return groups;
      },
      {} as Record<string, MediaItem[]>,
    );
  }
}
